import { useCallback, useState } from 'react';

interface LinesState {
    periodKey: string;
    open: ReadonlySet<string>;
    pages: Readonly<Record<string, number>>;
}

export interface OpenStatementLines {
    isOpen: (trancode: string) => boolean;
    pageOf: (trancode: string) => number;
    onToggle: (trancode: string) => void;
    onPageChange: (trancode: string, page: number) => void;
}

const emptyState = (periodKey: string): LinesState => ({ periodKey, open: new Set(), pages: {} });

export function useOpenStatementLines(periodKey: string): OpenStatementLines {
    const [state, setState] = useState<LinesState>(() => emptyState(periodKey));
    const current = state.periodKey === periodKey ? state : emptyState(periodKey);
    if (current !== state) setState(current);

    const isOpen = useCallback((trancode: string) => current.open.has(trancode), [current]);
    const pageOf = useCallback((trancode: string) => current.pages[trancode] ?? 1, [current]);
    const onToggle = useCallback((trancode: string) => {
        setState((previous) => {
            const open = new Set(previous.open);
            if (!open.delete(trancode)) open.add(trancode);
            return { ...previous, open };
        });
    }, []);
    const onPageChange = useCallback((trancode: string, page: number) => {
        setState((previous) => ({ ...previous, pages: { ...previous.pages, [trancode]: page } }));
    }, []);

    return { isOpen, pageOf, onToggle, onPageChange };
}
